/**
 * EmoteOverlay.tsx — full-screen overlay that animates emoji reactions
 * floating up from where they were anchored. Listens to incoming
 * EmoteReaction events via a controlled `recentEmotes` prop.
 *
 * Each emote stays on screen ~2s, animated with Framer Motion.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

export type EmoteEvent = {
  id: string;
  emoji: string;
  /** Anchor coordinates (0..1 normalized); defaults to center. */
  x?: number | undefined;
  y?: number | undefined;
  /** Wall-clock receipt time (ms). */
  at: number;
};

type Props = {
  emotes: readonly EmoteEvent[];
};

const VISIBLE_MS = 2400;

export function EmoteOverlay({ emotes }: Props) {
  const [visible, setVisible] = useState<readonly EmoteEvent[]>([]);

  useEffect(() => {
    // Append new emotes that aren't already shown; auto-prune after VISIBLE_MS.
    const known = new Set(visible.map((e) => e.id));
    const additions = emotes.filter((e) => !known.has(e.id));
    if (additions.length === 0) return;
    setVisible((prev) => [...prev, ...additions]);
    const timer = setTimeout(() => {
      const cutoff = Date.now() - VISIBLE_MS;
      setVisible((prev) => prev.filter((e) => e.at > cutoff));
    }, VISIBLE_MS + 100);
    return () => clearTimeout(timer);
  }, [emotes, visible]);

  return (
    <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
      <AnimatePresence>
        {visible.map((e) => {
          const left = `${(e.x ?? 0.5) * 100}%`;
          const top = `${(e.y ?? 0.5) * 100}%`;
          return (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, scale: 0.4, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: -120 }}
              exit={{ opacity: 0, scale: 0.6, y: -180 }}
              transition={{ duration: VISIBLE_MS / 1000, ease: 'easeOut' }}
              style={{ left, top, position: 'absolute' }}
              className="text-6xl select-none"
            >
              {e.emoji}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
